import React, { useEffect, useState } from "react";
import { FormField } from "./ContactForm";
import Button from "./Button";

const InitialPopUp = () => {
  const [isActive, setIsActive] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    organization: "",
    services: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");

  useEffect(() => {
    // Show popup on first visit (or when flag not set)
    try {
      const seen = localStorage.getItem("initialPopupSeen");
      const force = typeof window !== "undefined" && window.location && window.location.search && window.location.search.indexOf("showPopup=1") !== -1;
      if (!seen || force) {
        // 5 second delay before showing popup
        setTimeout(() => setIsActive(true), 15000);
      }
    } catch (e) {
      setIsActive(true);
    }
  }, []);

  const closePopup = () => {
    setIsActive(false);
    try {
      localStorage.setItem("initialPopupSeen", "1");
    } catch (e) {
      // ignore
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^\+\d{1,4}\s?\d{4,14}$/;
    return phoneRegex.test(phone.replace(/\s/g, ""));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = "Name is required";
    else if (form.name.trim().length < 2) newErrors.name = "Name must be at least 2 characters";

    if (!form.email.trim()) newErrors.email = "Email is required";
    else if (!validateEmail(form.email)) newErrors.email = "Please enter a valid email address";

    if (form.phone.trim() && !validatePhone(form.phone)) newErrors.phone = "Phone number must include country code (e.g., +91 9876543210)";

    if (form.organization.trim() && form.organization.trim().length < 2) newErrors.organization = "Organization name must be at least 2 characters";

    if (form.message.trim() && form.message.trim().length < 10) newErrors.message = "Message must be at least 10 characters";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formFields = [
    {
      id: "01",
      label: "What's your name?",
      name: "name",
      type: "text",
      placeholder: "John Doe*",
      required: true,
      isLanding: true,
      delay: 0.2,
    },
    {
      id: "02",
      label: "What's your email?",
      name: "email",
      type: "email",
      placeholder: "john@email.com",
      required: true,
      isLanding: false,
      delay: 0,
    },
    {
      id: "03",
      label: "What's your contact number?",
      name: "phone",
      type: "text",
      placeholder: "+1 987 654 3210",
      required: false,
      isLanding: false,
      delay: 0,
    },
    {
      id: "04",
      label: "What's the name of your organization?",
      name: "John Doe Co.",
      type: "text",
      placeholder: "John Doe Co.",
      required: false,
      isLanding: false,
      delay: 0,
    },
    {
      id: "05",
      label: "What services are you looking for?",
      name: "services",
      type: "text",
      placeholder: "SEO, Leads, Social Media etc.",
      required: false,
      isLanding: false,
      delay: 0,
    },
    {
      id: "06",
      label: "Describe any other requirements you have.",
      name: "message",
      type: "text",
      placeholder: "Mention your requirements here...",
      required: false,
      isTextarea: true,
      isLanding: false,
      delay: 0,
    },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    if (errors[name]) setErrors({ ...errors, [name]: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    setSubmitMessage("");
    try {
      // send to production function endpoint
      const endpoint = "https://faas-server-itun.onrender.com/form-submit";
      const payload = {
        name: form.name || "",
        contactNumber: form.phone || "",
        mobileNumber: form.phone || "",
        companyName: form.organization || "",
        city: "",
        email: form.email || "",
        servicesInterested: form.services || "",
        message: form.message || "",
        sourceWebsite: "digicots",
      };

      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      console.log(res)

      if (!res.ok) {
        const errText = await res.text().catch(() => "");
        throw new Error(`Request failed: ${res.status} ${errText}`);
      }

      // optional: parse response if needed
      let data = null;
      try {
        data = await res.json();
      } catch (e) {
        data = null;
      }

      console.log("Initial popup form submitted:", payload, data);
      setSubmitMessage((data && data.message) || "Thank you! Your message has been sent successfully.");
      setForm({ name: "", email: "", phone: "", organization: "", services: "", message: "" });
      setTimeout(() => {
        setSubmitMessage("");
        closePopup();
      }, 1500);
    } catch (err) {
      console.error("Initial popup submit error:", err);
      setSubmitMessage("Sorry, there was an error sending your message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isActive) return null;

  return (
    <div className={`fixed inset-0 z-[1000] flex items-center justify-center pointer-events-auto`}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={closePopup} />

      {/* Modal panel - constrained height so content fits on-screen */}
      <div data-lenis-prevent className="relative w-full max-w-5xl mx-4 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 shadow-2xl rounded-md overflow-hidden max-h-[90vh] overflow-y-auto ">
        {/* Close button */}
        <button
          onClick={closePopup}
          className="absolute top-4 right-4 z-20 w-10 h-10 flex items-center justify-center text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white transition-all duration-300 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 shadow-lg hover:shadow-xl hover:scale-110"
          aria-label="Close form"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="grid md:grid-cols-2 h-full">
          {/* Left content column (heading, bullets, illustration) - premium styling */}
          <div className="relative p-8 md:p-12 bg-gradient-to-br from-white/90 to-zinc-50 dark:from-zinc-900 dark:to-zinc-900 flex flex-col gap-6 overflow-auto">
            

            <div className="grid grid-cols-1 gap-3 mt-2">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
                  <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414L8.414 15 5 11.586a1 1 0 111.414-1.414L8.414 12.17l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                </div>
                <p className="text-sm text-zinc-700 dark:text-zinc-300">Get personalized consultancy packages tailored to suit your brand's specific marketing needs.</p>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
                  <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414L8.414 15 5 11.586a1 1 0 111.414-1.414L8.414 12.17l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                </div>
                <p className="text-sm text-zinc-700 dark:text-zinc-300">Grow your brand with proven strategies & frameworks that amplify your digital presence and catalyze growth.</p>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
                  <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414L8.414 15 5 11.586a1 1 0 111.414-1.414L8.414 12.17l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                </div>
                <p className="text-sm text-zinc-700 dark:text-zinc-300">Work with professionals who understand your unique challenges and goals.</p>
              </div>
            </div>

            <div className="mt-0 md:mt-auto">
              <div className="rounded-md hidden md:block ">
                <img src="/new-final-data/wolf.png" alt="illustration" className="w-full h-44 md:h-56 object-cover rounded-md opacity-95 relative -top-2 scale-[1.5]" />
              </div>
            </div>
          </div>

          {/* Right form column styled like reference - constrained so it always fits */}
          <div className="p-6 md:p-8 flex items-center justify-center">
            <div className="w-full max-w-md text-black dark:text-white rounded-sm shadow-lg overflow-hidden border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-transparent">
              <div className="bg-[#EE005B] text-white px-4 py-3 font-semibold text-center">GET YOUR CUSTOM QUOTE NOW!</div>

              <div className="p-6 max-h-[72vh] md:max-h-[70vh] overflow-y-auto">
                <form onSubmit={handleSubmit} className="space-y-4 text-black dark:text-white">
                  {formFields.map((field) => (
                    <FormField
                      key={field.name}
                      field={field}
                      value={form[field.name]}
                      onChange={handleChange}
                      errors={errors}
                    />
                  ))}

                  {submitMessage && (
                    <div className={`p-3 rounded ${submitMessage.includes("error") ? "bg-red-50 text-red-800" : "bg-green-50 text-green-800"}`}>
                      {submitMessage}
                    </div>
                  )}

                  <div className="flex items-center justify-between gap-4">
                    <hr className="h-px w-[60%] bg-white/20 border-0" />
                    <Button className={"text-sm"} disabled={isSubmitting}>{isSubmitting ? "Sending..." : "SUBMIT"}</Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InitialPopUp;